import { IUnitPaymentPlan, PaymentType } from "../../../types/models/economics";

/**
 * The arithmetic of a payment plan, shared by the editor and the table.
 *
 * Mirrors `UnitPaymentPlan.validate_parts` in ancana_api so the drawer can say
 * what is wrong while the person is still typing, instead of after a save the
 * server refuses. The server stays the authority; this only gets there first.
 *
 * Each payment carries its own type, because real plans mix them ("30%
 * enganche + 4 mensualidades de €10,000 + remanente"). Without the price only
 * the percentages can be checked: they never pass 100, and a plan made only of
 * percentages has to reach it unless it ends in the remaining balance.
 */

type PlanParts = Pick<IUnitPaymentPlan,
  'down_payment' | 'down_payment_type' | 'monthly_installment' | 'installment_count'
  | 'installment_type' | 'delivery_payment' | 'delivery_payment_type'>;

type Amount = string | number | null | undefined;

const isSet = (value: Amount) => value !== null && value !== undefined && value !== '';

const present = (value: Amount) => isSet(value) && Number(value) !== 0;

/** The payments the plan actually has, the installment run as its whole share. */
function payments(plan: PlanParts): { amount: number, type: PaymentType }[] {
  const list: { amount: Amount, type: PaymentType }[] = [
    { amount: plan.down_payment, type: plan.down_payment_type },
    {
      amount: isSet(plan.monthly_installment) && present(plan.installment_count)
        ? Number(plan.monthly_installment) * Number(plan.installment_count) : null,
      type: plan.installment_type,
    },
  ];
  if (plan.delivery_payment_type !== 'remainder') {
    list.push({ amount: plan.delivery_payment, type: plan.delivery_payment_type });
  }
  return list.filter(p => isSet(p.amount)).map(p => ({ amount: Number(p.amount), type: p.type }));
}

/** The share of the price the percentage payments add up to. */
export function percentageShare(plan: PlanParts): number {
  return payments(plan).filter(p => p.type === 'percentage').reduce((sum, p) => sum + p.amount, 0);
}

/** What the server would refuse this plan for, or null when it is coherent. */
export function planProblem(plan: PlanParts): string | null {
  if (present(plan.monthly_installment) !== present(plan.installment_count)) {
    return 'Installments need both an amount and a count, or neither.';
  }
  const remainder = plan.delivery_payment_type === 'remainder';
  if (remainder && isSet(plan.delivery_payment)) {
    return 'A remaining balance has no amount of its own; leave it empty.';
  }
  const paid = payments(plan);
  // In hundredths, like the server's two-decimal fields, so 33.33 × 3 + 0.01 is 100.
  const share = Math.round(percentageShare(plan) * 100);
  const onlyPercentages = paid.length > 0 && !remainder && paid.every(p => p.type === 'percentage');
  if (share > 10000) {
    return `The percentages add up to ${formatNumber(share / 100)}%; they cannot pass 100%.`;
  }
  if (onlyPercentages && share !== 10000) {
    return `The percentages add up to ${formatNumber(share / 100)}%; a plan of only percentages `
      + 'must add up to 100%, or end in the remaining balance.';
  }
  return null;
}

function formatNumber(value: number): string {
  return value.toLocaleString('es-MX', { maximumFractionDigits: 2 });
}

/** One payment as the table shows it: `30%`, `$100,000.00`, or `Remaining balance`. */
export function formatPart(value: Amount, type: PaymentType): string {
  if (type === 'remainder') {
    return 'Remaining balance';
  }
  if (!isSet(value)) {
    return '—';
  }
  return type === 'percentage'
    ? `${formatNumber(Number(value))}%`
    : `$${Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The installment run, `20 × 2%`, or a dash for a plan without one. */
export function formatInstallments(plan: PlanParts): string {
  if (!present(plan.monthly_installment) || !present(plan.installment_count)) {
    return '—';
  }
  return `${plan.installment_count} × ${formatPart(plan.monthly_installment, plan.installment_type)}`;
}
